import { z } from "zod/v4";
import { clubDetailSchema, clubSchema } from "../schemas/club";
import { clubMemberSchema } from "../schemas/club-member";
import { communityTypeSchema } from "../schemas/shared";
import type { CommunityType, UpdateBoost, UpdateSubscription } from "../types";
import { fetchApi, fetchVoid, paginatedSchema } from "./internal";

export interface ClubsListParams {
	limit?: number;
	offset?: number;
	q?: string;
	moderationStatus?: "approved" | "pending_review" | "rejected";
	subscriptionTier?: "free" | "club" | "club_pro" | "verband";
	type?: CommunityType;
	deletion?: "live" | "hidden";
}

/** One club named on either side of a Meilisearch/Postgres index mismatch. */
const clubIndexEntrySchema = z.object({
	id: z.string(),
	name: z.string(),
	type: communityTypeSchema,
});

/**
 * Drift between the club search index and the clubs table. The counts are
 * exact; the lists are a capped sample the API takes from the same diff.
 */
const clubIndexStatsSchema = z.object({
	live_total: z.number(),
	meili_total: z.number(),
	missing: z.array(clubIndexEntrySchema),
	missing_count: z.number(),
	orphan_count: z.number(),
	orphans: z.array(clubIndexEntrySchema),
});

const clubPruneResultSchema = z.object({
	deleted: z.array(clubIndexEntrySchema),
	live_total: z.number(),
	meili_total: z.number(),
});

export type ClubIndexEntry = z.infer<typeof clubIndexEntrySchema>;
export type ClubIndexStats = z.infer<typeof clubIndexStatsSchema>;

export interface ClubMembersListParams {
	limit?: number;
	offset?: number;
	q?: string;
	isTrainer?: boolean;
}

export const clubs = {
	delete: (id: string) => fetchVoid(`/admin/clubs/${id}`, { method: "DELETE" }),
	restore: (id: string) => fetchVoid(`/admin/clubs/${id}/restore`, { method: "POST" }),
	get: (id: string) => fetchApi(`/admin/clubs/${id}`, clubSchema),
	indexStats: () => fetchApi("/admin/explore/clubs/stats", clubIndexStatsSchema),
	list: (params: ClubsListParams = {}) => {
		const search = new URLSearchParams();
		if (params.limit !== undefined) search.set("limit", String(params.limit));
		if (params.offset !== undefined) search.set("offset", String(params.offset));
		if (params.q) search.set("q", params.q);
		if (params.moderationStatus) search.set("moderation_status", params.moderationStatus);
		if (params.subscriptionTier) search.set("subscription_tier", params.subscriptionTier);
		if (params.type) search.set("type", params.type);
		if (params.deletion === "hidden") search.set("deletion", "hidden");
		const qs = search.toString();
		return fetchApi(`/admin/clubs${qs ? `?${qs}` : ""}`, paginatedSchema(clubSchema));
	},
	listMembers: (params: ClubMembersListParams = {}) => {
		const search = new URLSearchParams();
		if (params.limit !== undefined) search.set("limit", String(params.limit));
		if (params.offset !== undefined) search.set("offset", String(params.offset));
		if (params.q) search.set("q", params.q);
		if (params.isTrainer !== undefined) search.set("is_trainer", String(params.isTrainer));
		const qs = search.toString();
		return fetchApi(`/admin/members${qs ? `?${qs}` : ""}`, paginatedSchema(clubMemberSchema));
	},
	members: (clubId: string) =>
		fetchApi(`/admin/clubs/${clubId}/members`, z.array(clubMemberSchema)),
	pruneIndex: () =>
		fetchApi("/admin/explore/clubs/prune", clubPruneResultSchema, { method: "POST" }),
	reindex: () =>
		fetchApi("/admin/clubs/reindex", z.object({ reindexed: z.number() }), {
			method: "POST",
		}),
	update: (id: string, data: unknown) =>
		fetchApi(`/admin/clubs/${id}`, clubSchema, {
			body: JSON.stringify(data),
			method: "PUT",
		}),
	updateBoost: (id: string, data: UpdateBoost) =>
		fetchApi(`/admin/clubs/${id}/boost`, clubDetailSchema, {
			body: JSON.stringify(data),
			method: "PUT",
		}),
	updateSubscription: (id: string, data: UpdateSubscription) =>
		fetchApi(`/admin/clubs/${id}/subscription`, clubDetailSchema, {
			body: JSON.stringify(data),
			method: "PUT",
		}),
};
